import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Lock, Play, Radio } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LivePage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    checkAccessAndFetchVideos();
  }, [user, token]);

  const checkAccessAndFetchVideos = async () => {
    // Check if user has premium access
    if (!user) {
      setHasAccess(false);
      setLoading(false);
      return;
    }

    if (user.subscription_status === 'active') {
      setHasAccess(true);
      await fetchLiveVideos();
    } else {
      setHasAccess(false);
      setLoading(false);
    }
  };

  const fetchLiveVideos = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/videos/live`, { headers });
      setVideos(response.data);
    } catch (error) {
      console.error('Failed to fetch live videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-white">
        <Navbar />
        <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
          <p className="text-xl text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-slate-950 text-white">
        <Navbar />
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 py-24 text-center">
          <Lock className="w-20 h-20 text-red-600 mx-auto mb-8" />
          <h1 
            data-testid="live-heading"
            className="text-4xl sm:text-5xl font-bold mb-6"
            style={{ fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase' }}
          >
            Premium Live Content
          </h1>
          <p className="text-xl text-slate-400 mb-8" style={{ fontFamily: 'Inter, sans-serif' }}>
            Access exclusive live streams and unlisted content from Unfiltered Vision News.
            Subscribe to premium to unlock this content.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 text-left">
            <div className="p-6 bg-slate-900 rounded-sm border border-slate-800">
              <Radio className="w-10 h-10 text-red-600 mb-4" />
              <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Oswald, sans-serif' }}>Live Streams</h3>
              <p className="text-slate-400" style={{ fontFamily: 'Inter, sans-serif' }}>
                Watch our weekly live broadcasts and discussions.
              </p>
            </div>
            <div className="p-6 bg-slate-900 rounded-sm border border-slate-800">
              <Play className="w-10 h-10 text-red-600 mb-4" />
              <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Oswald, sans-serif' }}>Exclusive Content</h3>
              <p className="text-slate-400" style={{ fontFamily: 'Inter, sans-serif' }}>
                Access unlisted videos not available to the public.
              </p>
            </div>
            <div className="p-6 bg-slate-900 rounded-sm border border-slate-800">
              <Play className="w-10 h-10 text-red-600 mb-4" />
              <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Oswald, sans-serif' }}>Full Archive</h3>
              <p className="text-slate-400" style={{ fontFamily: 'Inter, sans-serif' }}>
                Complete access to all past live streams and recordings.
              </p>
            </div>
          </div>

          {user ? (
            <Link to="/pricing" data-testid="upgrade-to-premium-button">
              <Button 
                className="bg-red-600 hover:bg-red-700 text-white text-lg px-8 py-6 rounded-sm font-bold"
                style={{ fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase' }}
              >
                Upgrade to Premium
              </Button>
            </Link>
          ) : (
            <Link to="/register" data-testid="get-started-button">
              <Button 
                className="bg-red-600 hover:bg-red-700 text-white text-lg px-8 py-6 rounded-sm font-bold"
                style={{ fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase' }}
              >
                Get Started
              </Button>
            </Link>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <Radio className="w-10 h-10 text-red-600" />
            <h1 
              data-testid="live-streams-heading"
              className="text-4xl sm:text-5xl font-bold"
              style={{ fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase' }}
            >
              Live Streams
            </h1>
          </div>
          <p className="text-xl text-slate-400" style={{ fontFamily: 'Inter, sans-serif' }}>
            Exclusive live broadcasts and unlisted content for premium subscribers.
          </p>
        </div>

        {videos.length === 0 ? (
          <div className="text-center py-24">
            <p className="text-xl text-slate-400 mb-8">No live streams available yet. Check back soon!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {videos.map((video) => (
              <Link 
                key={video.id} 
                to={`/videos/${video.id}`}
                data-testid={`live-video-card-${video.id}`}
                className="group"
              >
                <div className="relative aspect-video rounded-sm overflow-hidden mb-4 border border-slate-800 hover:border-red-600" style={{ transition: 'border 0.3s' }}>
                  <img 
                    src={video.thumbnail_url} 
                    alt={video.title}
                    className="w-full h-full object-cover group-hover:scale-105" 
                    style={{ transition: 'transform 0.5s' }}
                  />
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center" style={{ transition: 'opacity 0.3s' }}>
                    <Play className="w-12 h-12 text-white" fill="white" />
                  </div>
                  <div className="absolute top-2 left-2 bg-red-600 text-white px-2 py-1 text-xs font-bold rounded-sm flex items-center gap-1" style={{ fontFamily: 'Oswald, sans-serif' }}>
                    <Radio className="w-3 h-3" />
                    LIVE
                  </div>
                </div>
                <h3 
                  className="text-lg font-bold mb-2 line-clamp-2 group-hover:text-red-600"
                  style={{ fontFamily: 'Oswald, sans-serif', transition: 'color 0.3s' }}
                >
                  {video.title}
                </h3>
                <p className="text-sm text-slate-400" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {formatDate(video.published_at)}
                </p>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LivePage;
